// A FileSystemDirectoryHandle is a live browser object, not data: it survives IndexedDB's structured
// clone but not JSON, so the app's usual browser-local store (localStorage) would persist an empty
// object and silently lose the grant.
const HANDLE_DB_NAME = "soneu-mail-merge";
const HANDLE_STORE_NAME = "folder-handles";
const HANDLE_KEY = "data-source-folder";

function openHandleDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(HANDLE_DB_NAME, 1);

    request.onupgradeneeded = () => request.result.createObjectStore(HANDLE_STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open the folder handle database."));
  });
}

/**
 * Resolves once the transaction has committed, not merely once the request succeeded, so a stored
 * handle is really on disk before the caller acts on it.
 */
async function withHandleStore(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest): Promise<unknown> {
  const db = await openHandleDb();

  try {
    return await new Promise<unknown>((resolve, reject) => {
      const transaction = db.transaction(HANDLE_STORE_NAME, mode);
      const request = run(transaction.objectStore(HANDLE_STORE_NAME));
      const fail = () => reject(transaction.error ?? new Error("Folder handle transaction failed."));

      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = fail;
      transaction.onabort = fail;
    });
  } finally {
    db.close();
  }
}

export async function getStoredFolderHandle(): Promise<FileSystemDirectoryHandle | null> {
  const stored = await withHandleStore("readonly", (store) => store.get(HANDLE_KEY));

  // instanceof rather than a cast: IndexedDB hands back `unknown`, and a runtime check is the only
  // honest way to narrow it. Callers reach this only where the API exists, so the constructor does.
  return stored instanceof FileSystemDirectoryHandle ? stored : null;
}

export async function storeFolderHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  await withHandleStore("readwrite", (store) => store.put(handle, HANDLE_KEY));
}

export async function clearStoredFolderHandle(): Promise<void> {
  await withHandleStore("readwrite", (store) => store.delete(HANDLE_KEY));
}
